import { Box, Button, Flex, IconFont, Tag, Text } from '@byecode/ui'
import { closestCenter, DndContext, type DragEndEvent, MouseSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
    type ActorEvent,
    DELAY_TYPE,
    EVENT_TYPE,
    type IndependentAction,
    type ScrollEventConfig,
    type TimerEventConfig
} from '@lighthouse/core'
import { eventTypeName, formatTimerEventLabel } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { clone } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'

import { ActionAdder } from '../ActionAdder'
import { AddAction } from './AddAction'
import { EventDelayLabel } from './constant'
import { EventConfig } from './EventConfig'
import * as SC from './styles'

const SortableActionAdder: React.FC<{
    action: IndependentAction
    allParentActions: IndependentAction[]
    onChange: (val: IndependentAction) => void
    onDuplicate: (id: string) => void
    onDelete: (id: string) => void
}> = ({ action, allParentActions, onChange, onDuplicate, onDelete }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: action.id
    })

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.6 : 1
            }}
        >
            <ActionAdder
                action={action}
                allParentActions={allParentActions}
                onChange={onChange}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                dragHandleProps={{ attributes, listeners, setActivatorNodeRef }}
            />
        </div>
    )
}

export interface EventActionsProps {
    actorEvent: ActorEvent
    onChange: (event: ActorEvent) => void
    onDelete: (id: string) => void
}

export const EventActions: React.FC<EventActionsProps> = ({ actorEvent, onChange, onDelete }) => {
    const { id, actions, type, config } = actorEvent

    const label = useMemo(() => {
        if (type === EVENT_TYPE.SCROLL_BOTTOM) {
            return eventTypeName[EVENT_TYPE.SCROLL_BOTTOM].replace('x', `${(config as ScrollEventConfig)?.bottom ?? 0}px`)
        }
        if (type === EVENT_TYPE.TIMER) {
            return formatTimerEventLabel(config as TimerEventConfig)
        }
        return eventTypeName[type]
    }, [config, type])

    const delayLabel = useMemo(() => {
        if (!config?.delayRunConfig?.type || config?.delayRunConfig?.type === DELAY_TYPE.NONE) {
            return
        }
        const delayTypeName = EventDelayLabel[config.delayRunConfig.type]
        return `${delayTypeName} ${config?.delayRunConfig?.time ?? 0}ms`
    }, [config])

    const handleAddAction = useCallback(
        (val: IndependentAction) => {
            const newActions = [...actions, val]
            onChange({ ...actorEvent, actions: newActions })
        },
        [actions, actorEvent, onChange]
    )

    const handleChangeAction = useCallback(
        (val: IndependentAction) => {
            const newActions = actions.map(action => {
                if (action.id === val.id) {
                    return val
                }
                return action
            })
            onChange({ ...actorEvent, actions: newActions })
        },
        [actions, actorEvent, onChange]
    )

    const handleDeleteAction = useCallback(
        (actionId: string) => {
            const newActions = actions.filter(a => a.id !== actionId)
            onChange({ ...actorEvent, actions: newActions })
        },
        [actions, actorEvent, onChange]
    )

    const handleDuplicateAction = useCallback(
        (actionId: string) => {
            const actionIndex = actions.findIndex(action => action.id === actionId)
            if (actionIndex === -1) {
                return
            }

            const duplicatedAction = clone(actions[actionIndex])
            duplicatedAction.id = nanoid()

            const newActions = [...actions]
            newActions.splice(actionIndex + 1, 0, duplicatedAction)
            onChange({ ...actorEvent, actions: newActions })
        },
        [actions, actorEvent, onChange]
    )

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 4
            }
        })
    )

    const handleSortEnd = useCallback(
        (ev: DragEndEvent) => {
            const { active, over } = ev
            if (!over) {
                return
            }

            const activeId = String(active.id)
            const overId = String(over.id)
            if (activeId === overId) {
                return
            }

            const oldIndex = actions.findIndex(a => a.id === activeId)
            const newIndex = actions.findIndex(a => a.id === overId)
            if (oldIndex === -1 || newIndex === -1) {
                return
            }

            const newActions = arrayMove(actions, oldIndex, newIndex)
            onChange({ ...actorEvent, actions: newActions })
        },
        [actions, actorEvent, onChange]
    )

    const handleEventConfig = useCallback(
        (val: ActorEvent) => {
            onChange(val)
        },
        [onChange]
    )

    return (
        <>
            <SC.EventWrapper>
                <Flex justifyContent="space-between" alignItems="center" px={8} py={5}>
                    <Flex alignItems="center" gap={6}>
                        <Text weight={400} size={14}>
                            {label}
                        </Text>
                        <EventConfig value={actorEvent} onChange={handleEventConfig} />
                        {delayLabel && (
                            <Tag fill={true} size={12} radius={4} padding="2px 8px" background="var(--color-gray-100)">
                                {delayLabel}
                            </Tag>
                        )}
                    </Flex>
                    <Flex gap={6} alignItems="center">
                        <Button
                            type="text"
                            icon={<IconFont type="Trash" size={16} fill="var(--color-gray-400)" />}
                            onClick={() => {
                                onDelete(id)
                            }}
                            radius={5}
                            style={{ padding: 2, width: 20, height: 20 }}
                        />
                    </Flex>
                </Flex>
                <Box>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToParentElement]}
                        onDragEnd={handleSortEnd}
                    >
                        <SortableContext items={actions.map(a => a.id)} strategy={verticalListSortingStrategy}>
                            {actions.map((action, index) => {
                                const allParentActions = actions.slice(0, index)
                                return (
                                    <SortableActionAdder
                                        key={action.id}
                                        action={action}
                                        allParentActions={allParentActions}
                                        onChange={handleChangeAction}
                                        onDuplicate={handleDuplicateAction}
                                        onDelete={handleDeleteAction}
                                    />
                                )
                            })}
                        </SortableContext>
                    </DndContext>
                    <AddAction onAdd={handleAddAction} />
                </Box>
            </SC.EventWrapper>
        </>
    )
}
